import React from "react";
import { Tooltip } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { CopyToClipboard } from "react-copy-to-clipboard/lib/Component";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { TimestampToDateMoment } from "../../../utils/timestampToDate";
import PageSpinner from "../../elements/spinner/PageSpinner";
import { StyledBreakWord } from "../../styles/styledBreakWord";
import { StyledCopy } from "../../styles/styledCopy";
import RenderStatus from "../../styles/styledStatus";
import {
  StyledCol,
  StyledScrollTable,
  StyledTable,
  StyledTableBody,
  StyledTableHead,
  StyledTableRow
} from "../../styles/styledTable";
import { StyledTooltip } from "../../styles/styledTooltip";

const SwapList = ({ swaps, notification, setNotification }) => {
  const { t } = useTranslation("swapPage");

  const history = useHistory();

  if (!swaps) {
    return (
      <PageSpinner
        position="center"
        pt="20"
        pb="20"
      />
    );
  }

  if (swaps.length === 0) {
    return <Alert severity="warning">{t("table.emptyData")}</Alert>;
  }

  // Logic hasn't changed, as it only works when clicking on one column
  const showExchangeDetails = (id) => {
    history.push("/exchange/" + id);
  };

  return (
    <StyledScrollTable className="swap-table_scroll">
      <StyledTable
        width="2640"
        size="14"
        className="swap-table"
      >
        <StyledTableHead
          col="13"
          scroll="auto"
          className="swap-table__head"
        >
          <div>
            Id
            <Tooltip
              title={t("table.tooltips.id")}
              arrow
              placement="top"
            >
              <StyledTooltip className="icon-light-help" />
            </Tooltip>
          </div>
          <div>
            Reference id
            <Tooltip
              title={t("table.tooltips.referenceId")}
              arrow
              placement="top"
            >
              <StyledTooltip className="icon-light-help" />
            </Tooltip>
          </div>
          <div>
            Exchange id
          </div>
          <div>
            {t("table.user")}
          </div>
          <div>
            {t("table.project")}
          </div>
          <div>
            {t("table.inService")}
          </div>
          <div>
            {t("table.inAmount")}
          </div>
          <div>
            {t("table.outService")}
          </div>
          <div>
            {t("table.outAmount")}
          </div>
          <div>
            {t("table.status")}
          </div>
          <div>
            {t("table.createdAt")}
          </div>
          <div>
            {t("table.updatedAt")}
          </div>
          <div>
            {t("table.note")}
          </div>
        </StyledTableHead>
        <StyledTableBody>
          {swaps.map((item, key) => (
            <StyledTableRow
              key={key}
              scroll="auto"
              col="12"
              className="swap-table__row"
              cursor="pointer"
            >
              <StyledCol
                inline
                data-title="id"
              >
                <CopyToClipboard
                  text={item.id}
                  onCopy={() => {
                    setNotification({ ...notification, visible: true, type: "success", message: t("table.copied") });
                  }}
                >
                  <StyledBreakWord as="p">
                    {item.id} <StyledCopy
                    title={t("table.copy")}
                    className="icon-light-copy"
                  />
                  </StyledBreakWord>
                </CopyToClipboard>
              </StyledCol>
              <StyledCol
                inline
                data-title="Reference id"
              >
                <CopyToClipboard
                  text={item.referenceId}
                  onCopy={() => {
                    setNotification({ ...notification, visible: true, type: "success", message: t("table.copied") });
                  }}
                >
                  <StyledBreakWord as="p">
                    {item.referenceId} <StyledCopy
                    title={t("table.copy")}
                    className="icon-light-copy"
                  />
                  </StyledBreakWord>
                </CopyToClipboard>
              </StyledCol>
              <StyledCol
                inline
                data-title="Exchange id"
                onClick={() => item.exchange?.id && showExchangeDetails(item.exchange.id)}
              >
                <StyledBreakWord as="p">
                  {item.exchange?.id ?? "-"}
                </StyledBreakWord>
              </StyledCol>
              <StyledCol
                inline
                data-title={t("table.user")}
              >
                <StyledBreakWord>
                  {item.inConnection.project.user.email}
                </StyledBreakWord>
              </StyledCol>
              <StyledCol
                data-title={t("table.project")}
              >
                {item.inConnection.project.name}
              </StyledCol>
              <StyledCol
                data-title={t("table.inService")}
              >
                {item.inConnection.service.name}
              </StyledCol>
              <StyledCol
                data-title={t("table.inAmount")}
              >
                {item.inAmount} {item.inCurrency.asset} {item.inCurrency.asset === item.inPaymentSystem.subName ? null : `(${item.inPaymentSystem.subName})`}
              </StyledCol>
              <StyledCol
                data-title={t("table.outService")}
              >
                {item.outConnection.service.name}
              </StyledCol>
              <StyledCol
                data-title={t("table.outAmount")}
              >
                {item.outAmount} {item.outCurrency.asset} {item.outCurrency.asset === item.outPaymentSystem.subName ? null : `(${item.outPaymentSystem.subName})`}
              </StyledCol>
              <StyledCol
                inline
                data-title={t("table.status")}
              >
                <RenderStatus status={item.status.value} />
              </StyledCol>
              <StyledCol
                data-title={t("table.createdAt")}
              >
                {TimestampToDateMoment(item.createdAt)}
              </StyledCol>
              <StyledCol
                data-title={t("table.updatedAt")}
              >
                {TimestampToDateMoment(item.updatedAt)}
              </StyledCol>
              <StyledCol
                data-title={t("table.note")}
                className="swap-table__note"
              >
                <p className="note">
                  {item.note}
                </p>
                {item.note && item.note.length > 25 &&
                  <Tooltip
                    title={item.note}
                    arrow
                    placement="top"
                  >
                    <StyledTooltip className="icon-light-info" />
                  </Tooltip>}
              </StyledCol>
            </StyledTableRow>
          ))}
        </StyledTableBody>
      </StyledTable>
    </StyledScrollTable>
  );
};

export default SwapList;